import type { ObjectClass, UserMetric } from '@/type';

import type { BarChartData } from '../type';

/**
 * 특정 extendClass에 대한 AI 카운트와 Human 카운트를 userMetrics에서 계산합니다.
 */
const calculateClassCountForPeriod = (
  userMetrics: UserMetric[],
  extendClassId: number,
  extendClasses?: ObjectClass[],
): { aiCount: number; humanCount: number } => {
  return userMetrics.reduce(
    (acc, cur) => {
      const isMatch =
        cur.extendClassID === extendClassId ||
        extendClasses?.find(extend => extend.id === cur.extendClassID);

      if (isMatch) {
        return {
          aiCount: acc.aiCount + cur.aiCountValue,
          humanCount: acc.humanCount + cur.humanCountValue,
        };
      }
      return acc;
    },
    { aiCount: 0, humanCount: 0 },
  );
};

/**
 * 기간별 통계 데이터를 생성합니다.
 */
export const calculatePeriodData = (
  extendClasses: ObjectClass[] | null | undefined,
  userMetrics: UserMetric[] | null | undefined,
): BarChartData[] => {
  if (!extendClasses || !userMetrics) return [];

  // timestamp별로 그룹화
  const groupedByTimestamp = userMetrics.reduce(
    (acc, cur) => {
      if (!acc[cur.createdAt]) {
        acc[cur.createdAt] = [];
      }
      acc[cur.createdAt].push(cur);
      return acc;
    },
    {} as Record<string, UserMetric[]>,
  );

  // timestamp별로 정렬하고 labelData 생성
  return Object.entries(groupedByTimestamp)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([timeStamp, userMetricsForTimestamp]) => {
      const labelData = extendClasses.reduce(
        (acc, item) => {
          const { aiCount, humanCount } = calculateClassCountForPeriod(
            userMetricsForTimestamp,
            item.id,
            item.extendClass,
          );

          acc[item.alias || item.name] = {
            aiCount,
            humanCount,
            color: item.color,
          };

          return acc;
        },
        {} as Record<
          string,
          { aiCount: number; humanCount: number; color: string }
        >,
      );

      return {
        timeStamp,
        labelData,
      };
    });
};
